import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField, toCustomFieldKey } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { toFullName } from '../../services/user';
import { IExportColumn } from '../Exporter/service';
import { D, O, UserTableColumn } from './columns';

export type UserExportOtherProps = O & {
  usersById: Record<string, Doc<IUser>>;
};

type RelationshipIdGetter = (d: D) => string | undefined;

const resolveRelatedUser = (
  getId: RelationshipIdGetter,
  d: D,
  o: UserExportOtherProps
): Doc<IUser> | undefined => {
  const id = getId(d);
  return id ? o.usersById[id] : undefined;
};

// Relationship columns (Manager, and any custom field of type
// 'relationship') render a live-lookup component on screen rather than
// plain text, so they can't be exported via their `cell`. Each expands into
// two export columns instead: one for the related user's name, one for
// their email, since a name alone doesn't uniquely identify a person.
export const toExportColumns = (
  columns: UserTableColumn[],
  customUserFields: CustomUserField[]
): IExportColumn<string, D, UserExportOtherProps>[] => {
  const relationshipIdGetters: Record<string, RelationshipIdGetter> = {
    managerId: (d) => d.data.managerId
  };
  customUserFields
    .filter((f) => f.type === 'relationship')
    .forEach((f) => {
      relationshipIdGetters[toCustomFieldKey(f.key)] = (d) => {
        const value = d.data.customFields[f.key];
        return typeof value === 'string' ? value : undefined;
      };
    });

  return columns.flatMap(
    (column): IExportColumn<string, D, UserExportOtherProps>[] => {
      const getRelationshipId = relationshipIdGetters[column.key];
      if (getRelationshipId) {
        const label = String(column.head());
        return [
          {
            key: `${column.key}:name`,
            head: () => `${label} - Name`,
            cell: (d, o) => {
              const u = resolveRelatedUser(getRelationshipId, d, o);
              return u ? toFullName(u.data) : '';
            }
          },
          {
            key: `${column.key}:email`,
            head: () => `${label} - Email`,
            cell: (d, o) => {
              const u = resolveRelatedUser(getRelationshipId, d, o);
              return u ? u.data.email : '';
            }
          }
        ];
      }

      return [
        {
          key: column.key,
          head: () => String(column.head()),
          cell: (d, o) => {
            const value = column.cell(d, o);
            if (
              typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value === 'boolean'
            ) {
              return value;
            }
            // Only relationship columns render non-text cells today, and
            // those are handled above — this is a blank-cell fallback for
            // any future column type that forgets to be added there too.
            return '';
          }
        }
      ];
    }
  );
};
